import React, { useState, useEffect, useRef } from "react";
import {
  CalendarLtr24Regular, CalendarCheckmark24Regular,
  DocumentBulletList24Regular, Settings24Regular, SignOut24Regular,
  ClipboardTask24Regular, CalendarDay24Regular, BookContacts24Regular,
  Person24Regular, Key24Regular, Shield24Regular,
  ChevronLeft24Regular, ChevronRight24Regular,
  TaskListSquareLtr24Regular, CalendarClock24Regular,
  CalendarEmpty24Regular, BookOpen24Regular,
  Wrench24Regular, PeopleTeam24Regular, LockClosed24Regular,
  WeatherSunny24Regular, WeatherMoon24Regular,
} from "@fluentui/react-icons";
import { FiDollarSign } from "react-icons/fi";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import { ThemeProvider, useTheme } from "./ThemeContext";
import SixersLogo             from "./Sixers_Logo.png";
import StudentPerformanceForm from "./StudentPerformanceForm";
import StudentMaster          from "./StudentMaster";
import UserMaster             from "./UserMaster";
import RoleMaster             from "./RoleMaster";
import FieldAuthorization     from "./FieldAuthorization";
import ConfigMaster           from "./ConfigMaster";
import ParentDashboard        from "./ParentDashboard";
import CoachPerformanceView   from "./CoachPerformanceView";
import AttendanceKiosk        from "./AttendanceKiosk";
import StudentRosterReport    from "./StudentRosterReport";
import AttendanceReport       from "./AttendanceReport";
import CoachAttendance        from "./CoachAttendance";
import BatchManagement             from "./BatchManagement";
import KioskCheckIn                from "./KioskCheckIn";
import PerformanceChangeLog        from "./PerformanceChangeLog";
import PromotionReadinessReport    from "./PromotionReadinessReport";
import CategoryPerformanceReport   from "./CategoryPerformanceReport";
import ExecutiveDashboard          from "./ExecutiveDashboard";
import FinancialDashboard          from "./FinancialDashboard";
import CoachEvaluation           from "./CoachEvaluation";
import BatchBulkUpload           from "./BatchBulkUpload";
import FuturisticBackground        from "./FuturisticBackground";
import bg6                         from "./Background images/6.jpg";

// ─── Outfit font injection ────────────────────────────────────────────────────
const OUTFIT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');`;

// ─── Role-gated menu config ───────────────────────────────────────────────────
const ALL_SECTIONS = [
  {
    label:"Executive Report", icon:DocumentBulletList24Regular, roles:["Admin"],
    children:[
      { label:"Performance Intelligence", icon:DocumentBulletList24Regular, roles:["Admin"] },
      { label:"Finance Dashboard", icon:DocumentBulletList24Regular, roles:["Admin"] },
    ],
  },
  {
    label:"Student Master", icon:PeopleTeam24Regular, roles:["Admin","Coach","Staff"],
    children:[{ label:"Student Master", icon:PeopleTeam24Regular, roles:["Admin","Coach","Staff"] }],
  },
  {
    label:"Performance", icon:TaskListSquareLtr24Regular, roles:["Admin","Coach","Staff"],
    children:[
      { label:"Performance Update",  icon:ClipboardTask24Regular,    roles:["Admin","Coach"] },
      { label:"Coach Attendance",    icon:BookOpen24Regular,         roles:["Admin","Coach","Staff"] },
    ],
  },
  {
    label:"Batches", icon:CalendarClock24Regular, roles:["Admin","Coach","Staff"],
    children:[
      { label:"Batch Management",  icon:CalendarEmpty24Regular,      roles:["Admin","Coach"] },
      { label:"Batch Upload",      icon:ClipboardTask24Regular,      roles:["Admin"] },
      { label:"Kiosk Check-In",    icon:CalendarCheckmark24Regular,  roles:["Admin","Coach","Staff"] },
    ],
  },
  {
    label:"Reports", icon:DocumentBulletList24Regular, roles:["Admin","Coach","Staff","Parent"],
    children:[
      { label:"Promotion Readiness",    icon:TaskListSquareLtr24Regular,  roles:["Admin","Coach"] },
      { label:"Category Performance",   icon:ClipboardTask24Regular,      roles:["Admin","Coach"] },
      { label:"Performance Report",     icon:BookContacts24Regular,       roles:["Admin","Coach"] },
      { label:"Student Roster",         icon:CalendarLtr24Regular,        roles:["Admin","Coach","Staff"] },
      { label:"Attendance Report",      icon:CalendarDay24Regular,        roles:["Admin","Coach","Staff"] },
      { label:"Progress Summary",       icon:DocumentBulletList24Regular, roles:["Admin","Coach"] },
      { label:"Performance Change Log", icon:ClipboardTask24Regular,      roles:["Admin","Coach"] },
      { label:"Coach Evaluation",       icon:ClipboardTask24Regular,      roles:["Admin","Parent"] },
    ],
  },
  {
    label:"Configuration", icon:Wrench24Regular, roles:["Admin"],
    children:[{ label:"Config Master", icon:Settings24Regular, roles:["Admin"] }],
  },
  {
    label:"Security", icon:LockClosed24Regular, roles:["Admin"],
    children:[
      { label:"Users",                icon:Person24Regular, roles:["Admin"] },
      { label:"Roles",                icon:Key24Regular,    roles:["Admin"] },
      { label:"Fields Authorization", icon:Shield24Regular, roles:["Admin"] },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  try {
    const b = parseInt((hex||"#034e7b").replace("#",""), 16);
    return `${(b>>16)&255}, ${(b>>8)&255}, ${b&255}`;
  } catch { return "3,78,123"; }
};

const MONTH_OPTIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FINANCE_YEARS = ["2025", "2026"];

function filterSections(user) {
  const role = user.user_role;
  const perms = user.permissions || [];
  
  // Admin always sees everything by default, unless you want even Admin to be restricted.
  // Usually for this kind of "Role Master", we want to respect the selections.
  // But we'll add a safety so existing Admins don't lose access if perms are empty.
  const isAdmin = role === "Admin";
  
  return ALL_SECTIONS
    .map((s) => {
      // Check section-level roles
      if (s.roles.length && !s.roles.includes(role)) return null;
      
      // Filter children by EITHER hardcoded roles OR dynamic permissions
      const children = s.children.filter((c) => {
        // Dynamic check: if it's in the permissions list, it's allowed.
        if (perms.includes(c.label)) return true;
        
        // Admin Master Override: Always allow Admins to see features explicitly coded for Admin.
        // This prevents administrators from being locked out of newly released modules.
        if (isAdmin && (!c.roles.length || c.roles.includes("Admin"))) {
          return true;
        }
        
        // Safety Fallback: Use hardcoded roles if permissions list is empty (initial state)
        if (perms.length === 0) {
           return !c.roles.length || c.roles.includes(role);
        }
        
        // If we have permissions but THIS page isn't in them, hide it.
        return false;
      });
      
      return children.length ? { ...s, children } : null;
    })
    .filter(Boolean);
}

// ─── Nav group header ─────────────────────────────────────────────────────────
function GroupHeader({ icon:Icon, label, isOpen, collapsed, onClick, secondaryColor, secondaryRGB, isDark }) {
  const [hov, setHov] = useState(false);
  const baseClr = isDark ? "rgba(255,255,255,0.58)" : "rgba(3,78,123,0.65)";
  const activeClr = isDark ? "rgba(255,255,255,0.92)" : "rgba(3,78,123,0.95)";
  const hovBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.05)";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center",
        gap: collapsed ? 0 : 9,
        padding: collapsed ? "8px 0" : "7px 10px",
        borderRadius:9, cursor:"pointer",
        justifyContent: collapsed ? "center" : "flex-start",
        background: isOpen ? `rgba(${secondaryRGB},0.12)` : hov ? hovBg : "transparent",
        transition:"background 0.14s",
        marginBottom:1,
      }}>
      <Icon style={{
        color: isOpen ? secondaryColor : baseClr,
        fontSize:18, flexShrink:0, transition:"color 0.14s",
      }} />
      {!collapsed && (
        <>
          <span style={{
            flex:1, fontSize:10, fontWeight:800,
            letterSpacing:"0.09em", textTransform:"uppercase",
            color: isOpen ? activeClr : baseClr,
            transition:"color 0.14s",
          }}>{label}</span>
          <ChevronRight24Regular style={{
            color: isDark ? "rgba(255,255,255,0.28)" : "rgba(3,78,123,0.25)", fontSize:14,
            transform: isOpen ? "rotate(90deg)" : "none",
            transition:"transform 0.22s cubic-bezier(0.4,0,0.2,1)",
          }} />
        </>
      )}
    </div>
  );
}

// ─── Nav item ─────────────────────────────────────────────────────────────────
function NavItem({ icon:Icon, label, isActive, collapsed, onClick, secondaryColor, primaryColor, secondaryRGB, indent, isDark }) {
  const [hov, setHov] = useState(false);
  const baseClr = isDark ? "rgba(255,255,255,0.60)" : "rgba(3,78,123,0.70)";
  const activeClr = isDark ? "white" : "#02304f";
  const hovBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.05)";

  if (collapsed) {
    const bg = isActive ? `rgba(${secondaryRGB},0.24)` : hov ? hovBg : "transparent";
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          width:34, height:34, borderRadius:9, margin:"0 auto 2px",
          display:"flex", alignItems:"center", justifyContent:"center",
          background:bg,
          border: isActive ? `1px solid rgba(${secondaryRGB},0.38)` : "1px solid transparent",
          cursor:"pointer", transition:"all 0.14s",
        }}>
        <Icon style={{
          color: isActive ? secondaryColor : baseClr,
          fontSize:16,
        }} />
      </div>
    );
  }

  const bg = isActive ? `rgba(${secondaryRGB},0.15)` : hov ? hovBg : "transparent";
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center", gap:9,
        padding:`6px 10px 6px ${indent ? 20 : 10}px`,
        borderRadius:"0 9px 9px 0",
        marginLeft:-8,
        boxShadow: isActive ? `inset 3px 0 0 ${secondaryColor}` : "inset 3px 0 0 transparent",
        background:bg,
        cursor:"pointer", transition:"all 0.14s",
        marginBottom:1,
      }}>
      <Icon style={{
        color: isActive ? secondaryColor : baseClr,
        fontSize:15, flexShrink:0, transition:"color 0.14s",
      }} />
      <span style={{
        fontSize:13, fontWeight:isActive ? 700 : 500,
        color: isActive ? activeClr : baseClr,
        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
      }}>{label}</span>
    </div>
  );
}

// ─── Sign-out row ─────────────────────────────────────────────────────────────
function SignOutRow({ collapsed, isActive, onClick, isDark }) {
  const [hov, setHov] = useState(false);
  const active = hov || isActive;
  const baseClr = isDark ? "rgba(255,255,255,0.52)" : "rgba(3,78,123,0.60)";
  const activeClr = isDark ? "rgba(255,255,255,0.90)" : "#02304f";
  const hovBg = isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.06)";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center", gap:9,
        padding: collapsed ? "8px 0" : "8px 10px",
        borderRadius:9, cursor:"pointer",
        justifyContent: collapsed ? "center" : "flex-start",
        background: active ? hovBg : "transparent",
        border: active ? (isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(3,78,123,0.18)") : "1px solid transparent",
        transition:"all 0.14s",
      }}>
      <SignOut24Regular style={{
        color: active ? activeClr : baseClr,
        fontSize:18, transition:"color 0.14s",
      }} />
      {!collapsed && (
        <span style={{
          fontSize:13, fontWeight:600,
          color: active ? activeClr : baseClr,
          transition:"color 0.14s",
        }}>Sign Out</span>
      )}
    </div>
  );
}

// ─── Quick-nav card (welcome screen) ─────────────────────────────────────────
function QuickCard({ Icon, label, colorRGB, onClick, primaryColor, isDark }) {
  const [hov, setHov] = useState(false);
  const cardBg   = isDark
    ? (hov ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.06)")
    : (hov ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.78)");
  const cardBdr  = isDark
    ? (hov ? `1.5px solid rgba(${colorRGB},0.40)` : "1.5px solid rgba(255,255,255,0.10)")
    : (hov ? `1.5px solid rgba(${colorRGB},0.22)` : "1.5px solid rgba(255,255,255,0.65)");
  const cardSh   = isDark
    ? (hov ? `0 14px 36px rgba(0,0,0,0.40), 0 0 0 1px rgba(${colorRGB},0.15)` : "0 4px 16px rgba(0,0,0,0.25)")
    : (hov ? `0 14px 36px rgba(0,40,100,0.14), 0 0 0 1px rgba(${colorRGB},0.08)` : "0 4px 16px rgba(0,40,100,0.07)");
  const labelClr = isDark ? "rgba(255,255,255,0.88)" : primaryColor;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding:"20px 18px", borderRadius:18, cursor:"pointer",
        background:cardBg,
        backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
        border:cardBdr, boxShadow:cardSh,
        display:"flex", flexDirection:"column", alignItems:"flex-start", gap:12,
        transform: hov ? "translateY(-4px)" : "none",
        transition:"all 0.18s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
      <div style={{
        width:42, height:42, borderRadius:13,
        background:`rgba(${colorRGB},0.12)`,
        border:`1.5px solid rgba(${colorRGB},${isDark ? "0.30" : "0.18"})`,
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow: hov ? `0 4px 12px rgba(${colorRGB},0.22)` : "none",
        transition:"box-shadow 0.18s",
      }}>
        <Icon style={{ color:`rgb(${colorRGB})`, fontSize:22 }} />
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:700, color:labelClr, lineHeight:1.25 }}>{label}</div>
        <div style={{ fontSize:11, color:"#9aabb8", marginTop:2, fontWeight:500 }}>Open →</div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// LandingPage
// ══════════════════════════════════════════════════════════════════════════════
function LandingPage({ currentUser, currentUserId, onLogout }) {
  const { config } = useAppConfig();
  const { isDark, toggleTheme } = useTheme();

  // Inject Outfit font once
  useEffect(() => {
    if (!document.getElementById("lp-outfit-font")) {
      const s = document.createElement("style");
      s.id = "lp-outfit-font";
      s.textContent = OUTFIT_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // Config-driven design tokens
  const primaryColor   = config.primary_color   || Colors.primaryBlue || "#034e7b";
  const secondaryColor = config.secondary_color  || Colors.logoYellow  || "#f7ce0b";
  const sidebarOpacity = parseFloat(config.sidebar_opacity || "0.50");
  const configFont     = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;
  const orgName        = config.org_name || "Sixers Cricket Academy";
  const logoSrc        = config.logo_url || SixersLogo;

  // Shell always uses Outfit
  const shellFont = "'Outfit', system-ui, sans-serif";

  const primaryRGB   = hexToRgb(primaryColor);
  const secondaryRGB = hexToRgb(secondaryColor);

  const role     = currentUser?.user_role || "";
  const sections = filterSections(currentUser);
  const initials = (currentUser?.full_name || "?")
    .split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  const [activePage,    setActivePage]    = useState("");
  const [openGroup,     setOpenGroup]     = useState("");
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [financeYear,   setFinanceYear]   = useState("2026");
  const [financeMonthsSelected, setFinanceMonthsSelected] = useState([...MONTH_OPTIONS]);
  const leftPaneRef = useRef(null);
  const [iconTop,   setIconTop]   = useState("50%");
  const financeMonths = financeMonthsSelected.length ? financeMonthsSelected : [...MONTH_OPTIONS];
  const allMonthsSelected = financeMonthsSelected.length === MONTH_OPTIONS.length;

  const toggleFinanceMonth = (month) => {
    setFinanceMonthsSelected((prev) => {
      if (prev.includes(month)) return prev.filter((m) => m !== month);
      return [...prev, month];
    });
  };

  const selectAllFinanceMonths = () => setFinanceMonthsSelected([...MONTH_OPTIONS]);
  const clearAllFinanceMonths = () => setFinanceMonthsSelected([]);

  useEffect(() => {
    if (leftPaneRef.current)
      setIconTop(`${leftPaneRef.current.getBoundingClientRect().height / 2}px`);
  }, []);

  const autoSelectSingle = (children) => { if (children.length === 1) setActivePage(children[0].label); };
  const toggleMenu  = () => setMenuCollapsed(p => !p);
  const toggleGroup = (group, children) => {
    if (openGroup === group) setOpenGroup("");
    else { setOpenGroup(group); autoSelectSingle(children); }
  };
  const handleLogout = () => {
    setActivePage("Logout");
    setOpenGroup("");
    setTimeout(() => onLogout && onLogout(), 700);
  };

  // Sidebar geometry
  const SIDE_W    = menuCollapsed ? 56 : 224;
  const sideAlpha = Math.min(sidebarOpacity + 0.30, 0.96);
  const sideBot   = Math.max(sidebarOpacity, 0.36);
  const sideBg    = `linear-gradient(170deg, rgba(${primaryRGB},${sideAlpha}) 0%, rgba(${primaryRGB},${sideBot}) 100%)`;

  const renderContent = () => {
    switch (activePage) {
      case "Performance Intelligence":     return <ExecutiveDashboard />;
      case "Finance Dashboard":
        return <FinancialDashboard selectedYear={financeYear} selectedMonths={financeMonths} isDark={isDark} hideHeader />;
      case "Coach Evaluation":        return <CoachEvaluation />;
      case "Student Master":          return <StudentMaster currentUserId={currentUserId} />;
      case "Performance Update":      return <StudentPerformanceForm currentUserId={currentUserId} onNavigate={setActivePage} />;
      case "Coach Attendance":        return <CoachAttendance currentUser={currentUser} />;
      case "Batch Management":        return <BatchManagement currentUser={currentUser} />;
      case "Batch Upload":            return <BatchBulkUpload currentUser={currentUser} />;
      case "Kiosk Check-In":          return <KioskCheckIn currentUser={currentUser} />;
      case "Promotion Readiness":    return <PromotionReadinessReport />;
      case "Category Performance":   return <CategoryPerformanceReport />;
      case "Performance Report":      return <CoachPerformanceView mode="dashboard" currentUser={currentUser} />;
      case "Student Roster":          return <StudentRosterReport />;
      case "Attendance Report":       return <AttendanceReport />;
      case "Progress Summary":        return <CoachPerformanceView mode="metrics" currentUser={currentUser} />;
      case "Performance Change Log":  return <PerformanceChangeLog currentUser={currentUser} />;
      case "Config Master":           return <ConfigMaster currentUserId={currentUserId} />;
      case "Parent Portal":           return <ParentDashboard currentUser={currentUser} onLogout={onLogout} />;
      case "Users":                   return <UserMaster currentUserId={currentUserId} />;
      case "Roles":                   return <RoleMaster currentUserId={currentUserId} />;
      case "Fields Authorization":    return <FieldAuthorization currentUserId={currentUserId} />;
      case "Logout":
        return (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
            <p style={{ color:"#8899aa", fontSize:15, fontFamily:shellFont }}>Signing out…</p>
          </div>
        );
      default:
        return (
          <div style={{
            display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center",
            minHeight:"100%", padding:"52px 32px 60px",
            fontFamily:shellFont, boxSizing:"border-box",
            /* Dot-grid background */
            backgroundImage:`radial-gradient(circle, rgba(${primaryRGB},0.10) 1.2px, transparent 1.2px)`,
            backgroundSize:"26px 26px",
          }}>
            {/* Logo card */}
            <div style={{
              width:96, height:96, borderRadius:26, marginBottom:28,
              background:"rgba(255,255,255,0.88)",
              backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
              border:"1.5px solid rgba(255,255,255,0.75)",
              boxShadow:`0 12px 40px rgba(${primaryRGB},0.14), 0 2px 8px rgba(${primaryRGB},0.06)`,
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              <img src={logoSrc} alt={orgName} style={{ width:66, objectFit:"contain", opacity:0.92 }} />
            </div>

            {/* Academy name */}
            <div style={{
              fontSize:11, fontWeight:800, color:welcomeText,
              letterSpacing:"0.14em", textTransform:"uppercase",
              marginBottom:4, opacity:0.55,
            }}>{orgName}</div>

            {/* Greeting */}
            <h1 style={{
              margin:"0 0 12px", fontSize:30, fontWeight:800,
              color:welcomeText, letterSpacing:"-0.03em", textAlign:"center",
            }}>
              Welcome back,{" "}
              <span style={{ color:secondaryColor }}>
                {currentUser?.full_name?.split(" ")[0] || "User"}
              </span>
            </h1>

            {/* Role badge */}
            <div style={{
              display:"inline-flex", alignItems:"center", gap:8,
              padding:"6px 18px", borderRadius:40, marginBottom:14,
              background:`rgba(${secondaryRGB},0.12)`,
              border:`1.5px solid rgba(${secondaryRGB},0.35)`,
            }}>
              <div style={{
                width:7, height:7, borderRadius:"50%", background:secondaryColor,
                boxShadow:`0 0 8px rgba(${secondaryRGB},0.65)`,
              }} />
              <span style={{
                fontSize:11, fontWeight:800, color:welcomeText,
                letterSpacing:"0.10em", textTransform:"uppercase",
              }}>{role || "User"}</span>
            </div>

            <p style={{
              color:welcomeMuted, fontSize:13, lineHeight:1.7,
              textAlign:"center", maxWidth:380, margin:"0 0 40px",
            }}>
              {config.org_tagline || "Select a section from the sidebar to get started."}
            </p>

            {/* Quick-access grid */}
            {role === "Admin" && (
              <>
                <div style={{
                  display:"flex", alignItems:"center", gap:10, marginBottom:18,
                }}>
                  <div style={{ height:1, width:48, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                  <span style={{
                    fontSize:10, fontWeight:700, color: isDark ? "rgba(255,255,255,0.40)" : `rgba(${primaryRGB},0.45)`,
                    letterSpacing:"0.10em", textTransform:"uppercase",
                  }}>Quick Access</span>
                  <div style={{ height:1, width:48, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                </div>
                <div style={{
                  display:"grid", gridTemplateColumns:"repeat(3, 170px)",
                  gap:16,
                }}>
                  {[
                    { label:"Performance Intelligence", page:"Performance Intelligence", Icon:DocumentBulletList24Regular, colorRGB:"3,78,123" },
                    { label:"Finance Dashboard",   page:"Finance Dashboard",   Icon:DocumentBulletList24Regular, colorRGB:"3,78,123" },
                    { label:"Student Master",       page:"Student Master",       Icon:PeopleTeam24Regular,         colorRGB:"79,70,229" },
                    { label:"Performance Update",   page:"Performance Update",   Icon:ClipboardTask24Regular,      colorRGB:"8,145,178" },
                    { label:"Batch Management",     page:"Batch Management",     Icon:CalendarEmpty24Regular,      colorRGB:"5,150,105" },
                    { label:"Config Master",        page:"Config Master",        Icon:Settings24Regular,           colorRGB:"124,58,237" },
                    { label:"User Master",          page:"Users",                Icon:Person24Regular,             colorRGB:"220,38,38" },
                  ].map(item => (
                    <QuickCard
                      key={item.page}
                      Icon={item.Icon}
                      label={item.label}
                      colorRGB={item.colorRGB}
                      primaryColor={primaryColor}
                      isDark={isDark}
                      onClick={() => setActivePage(item.page)}
                    />
                  ))}
                </div>
              </>
            )}

            {(role === "Coach" || role === "Staff") && (
              <>
                <div style={{
                  display:"flex", alignItems:"center", gap:10, marginBottom:18,
                }}>
                  <div style={{ height:1, width:48, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                  <span style={{
                    fontSize:10, fontWeight:700, color: isDark ? "rgba(255,255,255,0.40)" : `rgba(${primaryRGB},0.45)`,
                    letterSpacing:"0.10em", textTransform:"uppercase",
                  }}>Quick Access</span>
                  <div style={{ height:1, width:48, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 170px)", gap:16 }}>
                  {[
                    { label:"Performance Update", page:"Performance Update", Icon:ClipboardTask24Regular, colorRGB:"8,145,178" },
                    { label:"Kiosk Check-In",      page:"Kiosk Check-In",     Icon:CalendarCheckmark24Regular, colorRGB:"5,150,105" },
                    { label:"Student Roster",      page:"Student Roster",     Icon:CalendarLtr24Regular, colorRGB:"79,70,229" },
                    { label:"Attendance Report",   page:"Attendance Report",  Icon:CalendarDay24Regular, colorRGB:"220,38,38" },
                  ].filter(i => sections.flatMap(s => s.children).some(c => c.label === i.page))
                   .map(item => (
                    <QuickCard
                      key={item.page}
                      Icon={item.Icon}
                      label={item.label}
                      colorRGB={item.colorRGB}
                      primaryColor={primaryColor}
                      isDark={isDark}
                      onClick={() => setActivePage(item.page)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        );
    }
  };

  const pageBg = isDark
    ? "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)"
    : "linear-gradient(160deg, #eef4fc 0%, #f0f6ff 50%, #f5f9fe 100%)";
  const welcomeText = isDark ? "rgba(255,255,255,0.92)" : primaryColor;
  const welcomeMuted = isDark ? "rgba(255,255,255,0.50)" : "#9aabb8";

  return (
    <div style={{ display:"flex", height:"100vh", position:"relative", overflow:"hidden", fontFamily:shellFont }}>
      <style>{`
        @keyframes lp_haloBlue {
          0%,100% { box-shadow: 0 0 0 4px rgba(3,78,123,0.08), 0 0 14px 4px rgba(3,78,123,0.18); }
          50%     { box-shadow: 0 0 0 7px rgba(3,78,123,0.13), 0 0 28px 8px rgba(3,78,123,0.34); }
        }
        @keyframes lp_haloYellow {
          0%,100% { box-shadow: 0 0 0 4px rgba(247,206,11,0.12), 0 0 14px 4px rgba(247,206,11,0.28); }
          50%     { box-shadow: 0 0 0 7px rgba(247,206,11,0.20), 0 0 28px 8px rgba(247,206,11,0.52); }
        }
      `}</style>
      <FuturisticBackground />

      {/* ═══════════════ SIDEBAR ═══════════════ */}
      <div ref={leftPaneRef} style={{
        width:SIDE_W, flexShrink:0,
        margin: "14px 0 14px 14px",
        borderRadius: 24,
        position:"relative", overflow:"hidden",
        display:"flex", flexDirection:"column",
        boxShadow: isDark ? "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" : "0 20px 50px rgba(3,78,123,0.15), 0 0 0 1px rgba(255,255,255,0.6)",
        transition:"width 0.28s cubic-bezier(0.4,0,0.2,1)",
        zIndex: 100
      }}>
        {/* Sidebar Background (Image + Glass) */}
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          backgroundImage: `url(${bg6})`, backgroundSize: "cover", backgroundPosition: "center",
          opacity: isDark ? 0.12 : 0.06
        }} />
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          background: isDark ? "rgba(2,13,24,0.75)" : "rgba(255,255,255,0.75)",
          backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)"
        }} />

        {/* Yellow collapse knob */}
        <div onClick={toggleMenu} style={{
          position:"absolute", top:iconTop, transform:"translateY(-50%)",
          right: 8, width:24, height:24,
          background:secondaryColor, borderRadius:8,
          border: isDark ? `1px solid ${primaryColor}` : `1.5px solid white`,
          display:"flex", justifyContent:"center", alignItems:"center",
          cursor:"pointer", color:primaryColor,
          boxShadow:`0 0 12px rgba(${secondaryRGB},0.40)`,
          zIndex:20, transition: "0.2s"
        }}>
          {menuCollapsed ? <ChevronRight24Regular fontSize={14} /> : <ChevronLeft24Regular fontSize={14} />}
        </div>

        {/* Theme toggle — top-left icon */}
        <div onClick={toggleTheme} title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"} style={{
          position:"absolute", top:12, left:10, zIndex:20,
          width:28, height:28, borderRadius:8,
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.07)",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.14)",
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", transition:"all 0.2s",
        }}
          onMouseEnter={e=>{ e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.13)"; }}
          onMouseLeave={e=>{ e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.07)"; }}>
          {isDark
            ? <WeatherSunny24Regular style={{ color:secondaryColor, fontSize:15 }}/>
            : <WeatherMoon24Regular  style={{ color:primaryColor,   fontSize:15 }}/>}
        </div>

        {/* ─── Logo & org header ─── */}
        <div style={{
          padding: menuCollapsed ? "18px 0 14px" : "20px 14px 16px",
          textAlign:"center",
          borderBottom:"1px solid rgba(255,255,255,0.08)",
          flexShrink:0,
        }}>
          {/* Logo ring */}
          <div style={{
            width: menuCollapsed ? 44 : 82,
            height: menuCollapsed ? 44 : 82,
            borderRadius:"50%",
            border: isDark
              ? `2.5px solid rgba(${secondaryRGB},0.70)`
              : `2.5px solid rgba(${primaryRGB},0.55)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto",
            transition:"width 0.28s, height 0.28s",
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.82)",
            animation: isDark
              ? "lp_haloYellow 2.6s ease-in-out infinite"
              : "lp_haloBlue 2.6s ease-in-out infinite",
          }}>
            <img src={logoSrc} alt={orgName} style={{
              width: menuCollapsed ? 30 : 64,
              objectFit:"contain",
              transition:"width 0.28s",
              opacity:0.95,
            }} />
          </div>

          {/* Text below logo */}
          {!menuCollapsed && (
            <div style={{ marginTop:12 }}>
              <div style={{
                fontSize:11, fontWeight:800,
                color:secondaryColor,
                letterSpacing:"0.10em", textTransform:"uppercase",
                lineHeight:1.3,
              }}>{orgName}</div>
              <div style={{
                fontSize:10, fontWeight:500,
                color: isDark ? "rgba(255,255,255,0.42)" : "rgba(3,78,123,0.50)",
                letterSpacing:"0.06em",
                marginTop:2,
              }}>Management Portal</div>
            </div>
          )}
        </div>

        {/* ─── Navigation (scrollable) ─── */}
        <div style={{
          flex:1, overflowY:"auto", overflowX:"hidden",
          padding: menuCollapsed ? "10px 8px" : "10px 8px 10px 16px",
          scrollbarWidth:"none", msOverflowStyle:"none",
        }}>
          {sections.map((section) => {
            const isOpen = openGroup === section.label;
            return (
              <div key={section.label} style={{ marginBottom:3 }}>
                <GroupHeader
                  icon={section.icon}
                  label={section.label}
                  isOpen={isOpen}
                  collapsed={menuCollapsed}
                  onClick={() => toggleGroup(section.label, section.children)}
                  secondaryColor={secondaryColor}
                  secondaryRGB={secondaryRGB}
                  isDark={isDark}
                />
                {isOpen && (
                  <div style={{ paddingBottom:4 }}>
                    {section.children.map((child) => (
                      <NavItem
                        key={child.label}
                        icon={child.icon}
                        label={child.label}
                        isActive={activePage === child.label}
                        collapsed={menuCollapsed}
                        onClick={() => setActivePage(child.label)}
                        secondaryColor={secondaryColor}
                        primaryColor={primaryColor}
                        secondaryRGB={secondaryRGB}
                        indent={!menuCollapsed}
                        isDark={isDark}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ─── Bottom: user + appearance + sign out ─── */}
        <div style={{
          padding: menuCollapsed ? "10px 8px" : "10px 10px",
          borderTop:"1px solid rgba(255,255,255,0.08)",
          flexShrink:0,
        }}>
          {/* User chip */}
          {!menuCollapsed ? (
            <div style={{
              display:"flex", alignItems:"center", gap:10,
              padding:"10px 12px", borderRadius:12,
              background: isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)",
              border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(3,78,123,0.15)",
              marginBottom:8,
            }}>
              <div style={{
                width:34, height:34, borderRadius:"50%", flexShrink:0,
                background:`linear-gradient(135deg, rgba(${secondaryRGB},0.95), rgba(${secondaryRGB},0.55))`,
                border:`2px solid rgba(${secondaryRGB},0.35)`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:12, fontWeight:900, color:primaryColor,
                boxShadow:`0 2px 10px rgba(${secondaryRGB},0.30)`,
              }}>{initials}</div>
              <div style={{ flex:1, overflow:"hidden" }}>
                <div style={{
                  fontSize:13, fontWeight:700,
                  color: isDark ? "white" : "#02304f",
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                  letterSpacing:"-0.01em",
                }}>{currentUser?.full_name || "User"}</div>
                <div style={{
                  fontSize:10, color:secondaryColor, fontWeight:700,
                  letterSpacing:"0.06em", textTransform:"uppercase", marginTop:1,
                }}>{role}</div>
              </div>
            </div>
          ) : (
            <div style={{
              width:34, height:34, borderRadius:"50%",
              background:`linear-gradient(135deg, rgba(${secondaryRGB},0.95), rgba(${secondaryRGB},0.55))`,
              border:`2px solid rgba(${secondaryRGB},0.35)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:12, fontWeight:900, color:primaryColor,
              boxShadow:`0 2px 10px rgba(${secondaryRGB},0.30)`,
              margin:"0 auto 8px",
            }}>{initials}</div>
          )}

          {/* Sign Out — always last */}
          <SignOutRow
            collapsed={menuCollapsed}
            isActive={activePage === "Logout"}
            onClick={onLogout}
            isDark={isDark}
          />
        </div>
      </div>

        {/* ═══════════════ CONTENT ═══════════════ */}
      <div style={{ flex:1, overflowY:"auto" }}>
        {activePage === "Finance Dashboard" && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "12px 18px",
              background: isDark ? "rgba(2,13,26,0.86)" : "rgba(248,251,255,0.92)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderBottom: isDark ? "1px solid rgba(247,206,11,0.15)" : "1px solid rgba(3,78,123,0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0,
                background: `linear-gradient(135deg, ${secondaryColor}, #fff3a0)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 14px rgba(255,200,0,0.32)",
              }}>
                <FiDollarSign size={20} color={primaryColor} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: isDark ? secondaryColor : primaryColor, letterSpacing: "0.01em" }}>
                  Finance Dashboard
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.72)" : "rgba(3,78,123,0.72)" }}>
                  Select one or more months and a year
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 560px" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.75)" : "rgba(3,78,123,0.75)", whiteSpace: "nowrap" }}>
                  Months
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    paddingBottom: 2,
                    flex: 1,
                    scrollbarWidth: "thin",
                  }}
                >
                  <button
                    type="button"
                    onClick={selectAllFinanceMonths}
                    style={{
                      border: allMonthsSelected
                        ? (isDark ? "1px solid rgba(247,206,11,0.75)" : "1px solid rgba(3,78,123,0.65)")
                        : (isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)"),
                      background: allMonthsSelected
                        ? (isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.14)")
                        : (isDark ? "rgba(255,255,255,0.06)" : "white"),
                      color: isDark ? "#f1f5f9" : "#334155",
                      borderRadius: 999,
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={clearAllFinanceMonths}
                    style={{
                      border: isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)",
                      background: isDark ? "rgba(255,255,255,0.06)" : "white",
                      color: isDark ? "#f1f5f9" : "#334155",
                      borderRadius: 999,
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Clear All
                  </button>
                  {MONTH_OPTIONS.map((m) => {
                    const selected = financeMonthsSelected.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleFinanceMonth(m)}
                        style={{
                          border: selected
                            ? (isDark ? "1px solid rgba(247,206,11,0.75)" : "1px solid rgba(3,78,123,0.65)")
                            : (isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)"),
                          background: selected
                            ? (isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.14)")
                            : (isDark ? "rgba(255,255,255,0.06)" : "white"),
                          color: isDark ? "#f1f5f9" : "#334155",
                          borderRadius: 999,
                          padding: "5px 10px",
                          fontSize: 11,
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        {selected ? "✓ " : ""}
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.75)" : "rgba(3,78,123,0.75)" }}>
                Year{" "}
                <select
                  value={financeYear}
                  onChange={(e) => setFinanceYear(e.target.value)}
                  style={{
                    marginLeft: 6,
                    minWidth: 92,
                    background: isDark ? "rgba(255,255,255,0.08)" : "white",
                    border: isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)",
                    color: isDark ? "white" : "#334155",
                    padding: "6px 8px",
                    borderRadius: 8,
                    fontWeight: 700,
                  }}
                >
                  {FINANCE_YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
}

export default function LandingPageRoot(props) {
  return (
    <ThemeProvider>
      <LandingPage {...props} />
    </ThemeProvider>
  );
}
